import { h } from 'preact'
import { InstantSearchProductCard } from './instant-search-product-card'
import { InstantSearchProduct } from './instant-search.types'
import { useEffect, useRef, useState } from 'preact/hooks'

declare module 'preact' {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'x-carousel': any
    }
  }
}

interface InstantSearchResultsProps {
  'search-term'?: string
}

export function InstantSearchResults({ 'search-term': searchTerm }: InstantSearchResultsProps) {
  const [products, setProducts] = useState<InstantSearchProduct[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const carouselRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!searchTerm || searchTerm.length < 3) {
      setProducts([])
      return
    }

    const fetchProducts = async () => {
      setIsLoading(true)
      try {
        // Use full search API instead of predictive search to get all results
        const searchUrl = `/search?q=${encodeURIComponent(searchTerm)}&type=product`
        const response = await fetch(searchUrl)
        if (!response.ok) throw new Error('Search failed')

        const html = await response.text()
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')

        // Extract product handles from the main product grid only (not tiles or other sections)
        const productGrid = doc.querySelector('[data-product-grid]')
        if (!productGrid) {
          setProducts([])
          return
        }

        const productCards = productGrid.querySelectorAll('[data-product-card]')
        const productHandlesSet = new Set<string>()

        productCards.forEach(card => {
          const handle = card.getAttribute('data-product-handle')
          if (handle) {
            productHandlesSet.add(handle) // Set automatically deduplicates
          }
        })

        const productHandles = Array.from(productHandlesSet)
        const productPromises = productHandles.map(handle =>
          fetch(`/products/${handle}?view=ajax`)
            .then(res => res.json())
            .catch(() => null)
        )

        const productsData = await Promise.all(productPromises)
        const validProducts = productsData.filter(p => p !== null)
        setProducts(validProducts)
      } catch (error) {
        console.error('Instant search error:', error)
        setProducts([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchProducts()
  }, [searchTerm])

  useEffect(() => {
    if (carouselRef.current && products.length > 0) {
      const carousel = carouselRef.current as any
      if (carousel.flickity) {
        carousel.flickity.reloadCells()
        carousel.flickity.resize()
      }
    }

    window.dispatchEvent(new CustomEvent('instant-search:results-updated', {
      detail: { count: products.length, searchTerm: searchTerm || '', isLoading }
    }))
  }, [products, searchTerm, isLoading])

  useEffect(() => {
    const handleResize = () => {
      if (carouselRef.current) {
        const carousel = carouselRef.current as any
        if (carousel.flickity) {
          carousel.flickity.resize()
        }
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  if (!searchTerm || searchTerm.length < 3) {
    return null
  }

  if (!isLoading && products.length === 0) {
    return null
  }

  return (
    <div class="flex flex-col gap-y-1.25 w-full">
      <div class="flex items-center justify-between gap-x-gutter w-full">
        <div class="flex flex-col gap-y-1.5 w-full">
          <h4 class="h4 font-medium">Search Results {products.length > 0 ? `(${products.length})` : ''}</h4>
        </div>
      </div>

      {!isLoading && products.length > 0 && (
        <div class="w-full">
          <x-carousel
            ref={carouselRef}
            className="flex w-full h-fit"
            carousel-settings={`{
              "draggable": true,
              "freeScroll": false,
              "wrapAround": false,
              "groupCells": false,
              "fade": false,
              "adaptiveHeight": false,
              "accessibility": true,
              "cellAlign": "left",
              "prevNextButtons": ${products.length > 3},
              "pageDots": false
            }`}
          >
            {products.map(product => (
              <InstantSearchProductCard key={product.id} product={product}/>
            ))}
          </x-carousel>
        </div>
      )}
    </div>
  )
}
